import { useConfirm } from "components/ConfirmDialog";
import { PageTitle } from "components/PageTitle";
import { Button } from "components/ui/Button";
import { Link } from "components/ui/Link";
import { Loading } from "components/ui/Loading/Loading";
import { StatusBadge } from "components/ui/StatusBadge";
import { Table } from "components/ui/Table/Table";
import {
  type CreateCompanyGroupInput,
  useCompanyGroupMutations,
  useCompanyGroupsApi,
} from "hooks/useCompanyGroupsApi";
import { type FC, useState } from "react";
import {
  COMPANY_GROUP_COLORS,
  type CompanyGroupColor,
  companyGroupColorClass,
  companyGroupDotClass,
} from "utils/company-group-colors";
import { toastError } from "utils/toast-utils";

/**
 * 企業グループ一覧画面 (ISSUE-803 Phase 1C)。
 * @return {JSX.Element} 企業グループ一覧
 */
export const CompanyGroupsPage: FC = () => {
  const { data, loading, error, refetch } = useCompanyGroupsApi();
  const {
    createGroup,
    deleteGroup,
    loading: mutating,
  } = useCompanyGroupMutations();
  const { confirm, dialog: confirmDialog } = useConfirm();

  const [showCreate, setShowCreate] = useState(false);
  const [draft, setDraft] = useState<CreateCompanyGroupInput>({
    name: "",
    color: null,
    description: "",
  });

  const handleCreate = async () => {
    if (!draft.name.trim()) return;
    try {
      await createGroup({
        name: draft.name.trim(),
        color: draft.color || null,
        description: draft.description?.trim() || null,
      });
      setShowCreate(false);
      setDraft({ name: "", color: null, description: "" });
      await refetch();
    } catch (e) {
      toastError(
        `作成に失敗しました: ${e instanceof Error ? e.message : "不明"}`,
      );
    }
  };

  const handleDelete = async (id: string, name: string) => {
    const ok = await confirm({
      title: "企業グループを削除",
      message: `企業グループ "${name}" を削除します。所属企業は保持されますが、グループからは外れます。`,
      confirmText: "削除",
      variant: "destructive",
    });
    if (!ok) {
      return;
    }
    try {
      await deleteGroup(id);
      await refetch();
    } catch (e) {
      toastError(
        `削除に失敗しました: ${e instanceof Error ? e.message : "不明"}`,
      );
    }
  };

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="flex items-center justify-between mb-4">
        <PageTitle>企業グループ</PageTitle>
        <Button
          type="button"
          size="sm"
          onClick={() => setShowCreate(true)}
          disabled={mutating}
        >
          + 新規作成
        </Button>
      </div>

      {error && (
        <div className="alert alert-error mb-4">
          <span>エラー: {error.message}</span>
        </div>
      )}

      {/* 新規作成フォーム */}
      {showCreate && (
        <div className="mb-4 rounded-lg border border-slate-200 bg-white p-4">
          <h3 className="text-sm font-semibold mb-3">新規企業グループ作成</h3>
          <div className="space-y-3">
            <input
              type="text"
              placeholder="グループ名 (必須, 例: 優先度高)"
              value={draft.name}
              onChange={e => setDraft(s => ({ ...s, name: e.target.value }))}
              className="input input-bordered input-sm w-full md:w-96"
            />
            <div className="flex flex-wrap gap-2 items-center">
              <span className="text-xs text-base-content/60">色:</span>
              <button
                type="button"
                onClick={() => setDraft(s => ({ ...s, color: null }))}
                className={`px-2 py-1 rounded text-xs border ${
                  draft.color === null || draft.color === ""
                    ? "border-slate-700"
                    : "border-slate-300"
                }`}
              >
                無し
              </button>
              {COMPANY_GROUP_COLORS.map(c => (
                <button
                  type="button"
                  key={c}
                  onClick={() =>
                    setDraft(s => ({ ...s, color: c as CompanyGroupColor }))
                  }
                  className={`px-2 py-1 rounded text-xs ${companyGroupColorClass(c)} ${
                    draft.color === c
                      ? "ring-2 ring-offset-1 ring-slate-700"
                      : ""
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
            <textarea
              placeholder="説明 (任意)"
              value={draft.description ?? ""}
              onChange={e =>
                setDraft(s => ({ ...s, description: e.target.value }))
              }
              className="textarea textarea-bordered textarea-sm w-full md:w-96"
              rows={2}
            />
          </div>
          <div className="mt-3 flex gap-2 justify-end">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                setShowCreate(false);
                setDraft({ name: "", color: null, description: "" });
              }}
            >
              キャンセル
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={handleCreate}
              disabled={mutating || !draft.name.trim()}
            >
              作成
            </Button>
          </div>
        </div>
      )}

      {/* 一覧 */}
      <div className="rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden">
        <Table.Root density="compact" zebra stickyHeader>
          <Table.Head sticky>
            <tr>
              <Table.HeaderCell>グループ名</Table.HeaderCell>
              <Table.HeaderCell>所属企業</Table.HeaderCell>
              <Table.HeaderCell>更新日</Table.HeaderCell>
              <Table.HeaderCell>操作</Table.HeaderCell>
            </tr>
          </Table.Head>
          <Table.Body>
            {loading ? (
              <tr>
                <td colSpan={4} className="text-center py-8">
                  <Loading size="md" />
                </td>
              </tr>
            ) : data?.items.length === 0 ? (
              <tr>
                <td
                  colSpan={4}
                  className="text-center py-8 text-base-content/60"
                >
                  企業グループがまだ作成されていません
                </td>
              </tr>
            ) : (
              data?.items.map(g => (
                <Table.Row key={g.id}>
                  <Table.Cell>
                    <div className="flex items-center gap-2">
                      <span
                        className={`inline-block w-2.5 h-2.5 rounded-full ${companyGroupDotClass(g.color)}`}
                        title={g.color ?? "色未設定"}
                        aria-hidden
                      />
                      <Link to={`/settings/company-groups/${g.id}`}>
                        {g.name}
                      </Link>
                    </div>
                    {g.description && (
                      <div className="text-xs text-base-content/60 mt-1">
                        {g.description}
                      </div>
                    )}
                  </Table.Cell>
                  <Table.Cell>
                    <StatusBadge tone="soft-neutral">
                      {g.companyCount}
                    </StatusBadge>
                  </Table.Cell>
                  <Table.Cell>
                    {new Date(g.updatedAt).toLocaleDateString("ja-JP")}
                  </Table.Cell>
                  <Table.Cell>
                    <Button
                      type="button"
                      size="xs"
                      variant="destructive"
                      onClick={() => handleDelete(g.id, g.name)}
                      disabled={mutating}
                    >
                      削除
                    </Button>
                  </Table.Cell>
                </Table.Row>
              ))
            )}
          </Table.Body>
        </Table.Root>
      </div>
      {confirmDialog}
    </div>
  );
};

export default CompanyGroupsPage;
